/**
 * Format a number as Brazilian currency (BRL)
 */
export function formatCurrency(value: number | undefined | null): string {
  if (value === undefined || value === null) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * L-LOC.1b: valor de item de locação. Zero = complemento que acompanha a máquina → "Incluso".
 * null/undefined caem no formatCurrency (R$ 0,00), preservando o comportamento legado.
 */
export function formatItemValue(value: number | undefined | null): string {
  if (value === 0) return 'Incluso';
  return formatCurrency(value);
}

/** Resumo para seletores (último pedido + último preço), alinhado ao modal de produto. */
export function formatLastOrderCatalogSummary(
  lastQuantity: number | null | undefined,
  lastUnitPrice: number | null | undefined,
): string {
  const qtyStr = lastQuantity != null ? `${lastQuantity} un` : '—';
  const priceStr = lastUnitPrice != null ? formatCurrency(lastUnitPrice) : '—';
  return `Últ. pedido: ${qtyStr} · Preço: ${priceStr}`;
}

/**
 * Format a date string to Brazilian format
 * Uses parseISO for string dates to avoid timezone issues with YYYY-MM-DD format
 */
export function formatDate(date: string | Date | undefined | null): string {
  if (!date) return '-';
  // For string dates in YYYY-MM-DD format, use parseISO to avoid timezone issues
  const d = typeof date === 'string' 
    ? (date.match(/^\d{4}-\d{2}-\d{2}$/) ? parseISODate(date) : new Date(date))
    : date;
  return new Intl.DateTimeFormat('pt-BR').format(d);
}

/**
 * Parse ISO date string (YYYY-MM-DD) as local date to avoid timezone issues
 */
function parseISODate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Format a datetime string to Brazilian format with time
 */
export function formatDateTime(date: string | Date | undefined | null): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(d);
}

/**
 * Format CPF (000.000.000-00)
 */
export function formatCPF(cpf: string | undefined | null): string {
  if (!cpf) return '';
  const numbers = cpf.replace(/\D/g, '');
  if (numbers.length !== 11) return cpf;
  return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * Máscara progressiva CPF/CNPJ para inputs (§I.94):
 * ≤11 dígitos formata como CPF (000.000.000-00), acima como CNPJ
 * (00.000.000/0000-00), cortando em 14 dígitos. Aceita valor parcial
 * durante a digitação.
 */
export function maskCpfCnpj(value: string): string {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 11) {
    // CPF: 000.000.000-00
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
  }
  // CNPJ: 00.000.000/0000-00
  if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
  return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
}

/**
 * Format CNPJ (00.000.000/0000-00)
 */
export function formatCNPJ(cnpj: string | undefined | null): string {
  if (!cnpj) return '';
  const numbers = cnpj.replace(/\D/g, '');
  if (numbers.length !== 14) return cnpj;
  return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

/**
 * Format phone number
 */
export function formatPhone(phone: string | undefined | null): string {
  if (!phone) return '';
  const numbers = phone.replace(/\D/g, '');
  if (numbers.length === 11) {
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (numbers.length === 10) {
    return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return phone;
}

/**
 * Format CEP (00000-000)
 */
export function formatCEP(cep: string | undefined | null): string {
  if (!cep) return '';
  const numbers = cep.replace(/\D/g, '');
  if (numbers.length !== 8) return cep;
  return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
}

/**
 * Format percentage
 */
export function formatPercentage(value: number | undefined | null, decimals = 2): string {
  if (value === undefined || value === null) return '0%';
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format a customer/supplier address into a standardized string.
 * Handles null/empty fields gracefully.
 */
export interface AddressFields {
  address?: string | null;
  address_number?: string | null;
  address_complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
}

export function formatCustomerAddress(data: AddressFields): string {
  let street = data.address || '';
  if (data.address_number) street += `, ${data.address_number}`;
  if (data.address_complement) street += ` - ${data.address_complement}`;

  const location = data.city && data.state
    ? `${data.city} - ${data.state}`
    : data.city || data.state || '';

  const parts = [street, data.neighborhood, location].filter(Boolean);
  return parts.join(', ') || 'Endereço não informado';
}
